import { useState, useEffect, useCallback } from "react";
import { Printer } from "lucide-react";
import { useTranslation } from "../../i18n";
import PrinterSelectModal from "../PrinterSelectModal";
import { getSavedNetworkPrinter, clearNetworkPrinter, detectPrintMode } from "../helpers/networkPrint";
import { getSavedLocalPrinter, clearPrintModePreference } from "../helpers/printAgent";

/** Compact printer status pill for the dispatch scan screens.
 *
 *  With auto-print on, the confirm-ship screen prints the moment it mounts,
 *  so its printer picker is unreachable — this pill gives the operator a
 *  place to choose the printer BEFORE an order is opened. Self-contained:
 *  detects the active print mode the same way the confirm-ship screen does
 *  (saved network printer → local agent → none) and opens the shared
 *  PrinterSelectModal. The selection lands in localStorage, which every
 *  dispatch print path re-reads at print time, so a choice made here is
 *  what auto-print uses. */
/** @param onModeChange fired whenever the operator changes/clears the
 *  printer, so a host that caches the print mode (confirm-ship holds it in
 *  a ref for the print call) can re-detect instead of printing to the
 *  target the operator just moved away from. */
const DispatchPrinterButton = ({ onModeChange }: { onModeChange?: () => void } = {}) => {
    const { t } = useTranslation();
    // undefined = detection still running, null = nothing found.
    const [printMode, setPrintMode] = useState<"local" | "network" | null | undefined>(undefined);
    const [showSelect, setShowSelect] = useState(false);

    const detectPrinter = useCallback(async () => {
        setPrintMode(await detectPrintMode());
    }, []);
    useEffect(() => { detectPrinter(); }, [detectPrinter]);

    const label = printMode === "network"
        ? (getSavedNetworkPrinter()?.friendlyName ?? t("select_printer"))
        : printMode === "local"
            ? (getSavedLocalPrinter() ?? t("local_printer"))
            : t("select_printer");

    return (
        <>
            <button
                className={`dispatch-printer-select-btn ${printMode ? "dispatch-printer-select-btn--connected" : ""}`}
                onClick={() => setShowSelect(true)}
            >
                <Printer size={18} />
                <span>{label}</span>
            </button>
            {showSelect && (
                <PrinterSelectModal
                    autoPrint={false}
                    printMode={printMode ?? null}
                    onSelect={() => { setShowSelect(false); setPrintMode("network"); onModeChange?.(); }}
                    onConnectLocal={() => { setShowSelect(false); setPrintMode("local"); onModeChange?.(); }}
                    // Unlike the wipe module (which gates printing on its own
                    // session state), dispatch prints from later screens that
                    // re-read localStorage — so disconnect must clear the
                    // saved selection + preference to actually take effect there.
                    onDisconnect={() => { setShowSelect(false); clearNetworkPrinter(); clearPrintModePreference(); detectPrinter(); onModeChange?.(); }}
                    onClose={() => { setShowSelect(false); detectPrinter(); onModeChange?.(); }}
                />
            )}
        </>
    );
};

export default DispatchPrinterButton;
